/**
 * storeRegistry (Festipod glue) — the SDK owns placement; the app maps
 * entity → scope. This file keeps ONLY the Festipod domain mapping (entity kind
 * → scope) and re-exports the SDK registry surface so existing callers stay
 * unchanged. The consumer wiring the SDK needs (session + id normalization) is
 * declared in the ONE `configure` call, in `./ngSession`.
 */

// Side-effect import: `configure` runs at ngSession's module evaluation, and it
// must have run before ANY registry call. Importing it here makes that ordering
// a fact of the module graph rather than a convention.
import './ngSession';
import { storeRegistry as sdkStoreRegistry } from '@ng-eventually/polyfill';
import type { Nuri, NuriLike } from '@ng-eventually/polyfill';
import { canonicalDocumentId } from './documentNuri';
import { resolveOncePerKey } from './resolveOnce';

export type Scope = 'public' | 'protected' | 'private';

/** Domain entity kinds and the scope (= future store) each one lives in. */
export type EntityKind = 'event' | 'meetingPoint' | 'profile' | 'profilePrivate' | 'participation' | 'connectionIndex';

/** Maps a domain entity to its scope, exactly per the authorization matrix. */
export function entityScope(kind: EntityKind): Scope {
  switch (kind) {
    case 'event':            // declared by the user → their public store
    case 'meetingPoint':     // hosted by the user → their public store
      return 'public';
    case 'profile':          // network profile → protected store
    case 'participation':    // participation → protected store
    case 'connectionIndex':  // connections index → protected store
      return 'protected';
    case 'profilePrivate':   // settings, email → private store
      return 'private';
  }
}

// --- Re-export the SDK's registry surface -----------------------------------
// Placement is named by SCOPE ALONE: a session belongs to one user, so no call
// here takes an identity. "My documents" means the signed-in session's, and the
// app has no way — and no need — to name whose they are.
export const {
  resolveWriteGraph,
  listMyEntityDocs,
  // SDK-shaped scope resolvers — the app asks by scope, the SDK resolves
  // placement (no store id ever crosses the boundary).
  resolveScopeGraph,
  // Per-entity document creation. The SDK itself files the creator's key on
  // create, so the app declares NO access policy here: reading is possession,
  // and the creator holds what it created.
  createEntityDoc,
} = sdkStoreRegistry;

// --- A document's inbox: opened ONCE, by this session, whatever the concurrency -
// A document only HAS an inbox if its owner opened one. The app opens one on the
// documents meant to RECEIVE deposits (its events), and the address this returns
// is what the owner reads and watches.
//
// ASKING TWICE IS NOT FREE. "Open the inbox of this document" answers with an
// address; two callers racing each other get two, and then the owner watches one
// while a deposit lands in the other — the sign-up is never seen. Nothing looked
// wrong at any single call site: creating an event opens its inbox, the
// materializer opens it to read, the watch opens it to subscribe, and the watch
// callback re-enters the materializer — four calls within a fraction of a second,
// none of them aware of the others.
//
// So the app resolves it exactly ONCE PER DOCUMENT for the whole session, and
// simultaneous callers AWAIT THAT SAME RESOLUTION instead of starting their own
// (`resolveOncePerKey`). This wrapper is the ONLY place the SDK call is made — the
// raw entry is not re-exported, so no call site can bypass it. The key is the
// document's CANONICAL form, so the same document named with and without its
// overlay suffix is one document here too.
//
// A session is one identity for its whole life, so a session-long memo can never
// hand one person another's address. A REJECTION is not memoized: it means the
// answer is UNKNOWN, so the next caller genuinely retries.
export const openDocumentInbox: (doc: NuriLike) => Promise<Nuri> = resolveOncePerKey(
  (doc: NuriLike) => canonicalDocumentId(doc),
  (doc: NuriLike) => sdkStoreRegistry.openDocumentInbox(doc),
  {
    onResolved: (doc, address) =>
      console.log(`[app][inbox] doc=${doc} → inbox=${address} (this session's only one)`),
    onJoined: doc =>
      console.log(`[app][inbox] doc=${doc} — joined the resolution already in flight (no second inbox)`),
  },
);
